import './DashboardScreen.css';
import './screens.css';
import { useLearner } from '@/state/learnerStore';
import type { LearnerState } from '@/types/learner';
import type { PersonaKey } from '@/constants/avatars';
import { PERSONAS_SEED } from '@/seed/personas';
import { SCENARIOS, SCENARIO_ORDER } from '@/seed/scenarios';
import type { ScenarioConfig } from '@/types/scenario';
import { appHref } from '@/lib/nav';

/**
 * Practice — the full role-play scenario index. One card per scenario,
 * driven entirely by the seed (src/seed/scenarios.ts + personas.ts), with
 * the same lock/mastery state the dashboard tiles use. Picking a scenario
 * routes into the live role-play at /roleplay/:scenarioId.
 *
 * Plain <a> (not <Link>) on the CTA: the coach↔persona transition forces a
 * full page load because CodeBaby's loader can't re-init on the same page.
 */
export default function PracticeScreen() {
  const learner = useLearner();
  const scenarios = SCENARIO_ORDER.map((id) => SCENARIOS[id]).filter((s): s is ScenarioConfig =>
    Boolean(s),
  );

  return (
    <div className="screen">
      <div>
        <p className="screen__eyebrow">Practice</p>
        <h1 className="screen__title">Role-play scenarios, on demand</h1>
        <p className="screen__sub">
          Every buyer in the book. Pick a customer, step into the room, and earn the meeting —
          voice-first, with a text fallback for crowded rooms. New personas unlock as you work
          through the modules.
        </p>
      </div>

      <section>
        <div className="persona-grid">
          {scenarios.map((scenario) => (
            <ScenarioCard key={scenario.scenario_id} scenario={scenario} learner={learner} />
          ))}
        </div>
      </section>
    </div>
  );
}

function ScenarioCard({ scenario, learner }: { scenario: ScenarioConfig; learner: LearnerState }) {
  const persona = PERSONAS_SEED[scenario.persona_id];
  const status = personaStatus(scenario.persona_id, learner);
  return (
    <article className={`persona-tile persona-tile--${persona.color_key} persona-tile--${status}`}>
      <div className="persona-tile__top">
        <div
          className={`persona-tile__avatar persona-tile__avatar--${persona.color_key}`}
          aria-hidden
        >
          {persona.display_name
            .split(' ')
            .map((p) => p[0])
            .join('')}
        </div>
        <span className={`persona-tile__badge persona-tile__badge--${status}`}>
          {status === 'mastered' && 'Mastered'}
          {status === 'unlocked' && 'Unlocked'}
          {status === 'locked' && 'Locked'}
        </span>
      </div>
      <h4 className="persona-tile__name">{persona.display_name}</h4>
      <p className="persona-tile__role">{scenario.short_label}</p>
      <p className="persona-tile__desc">{scenario.context_line}</p>
      <p className="scenario-card__tags">
        {scenario.mood_tags.map((tag) => (
          <span key={tag} className="scenario-card__tag">
            {tag}
          </span>
        ))}
      </p>
      {status === 'locked' ? (
        <p className="persona-tile__hint">{persona.unlock_hint}</p>
      ) : (
        <a href={appHref(`/roleplay/${scenario.scenario_id}`)} className="persona-tile__cta">
          {status === 'mastered' ? 'Warm back up →' : 'Start attempt →'}
        </a>
      )}
    </article>
  );
}

function personaStatus(key: PersonaKey, learner: LearnerState): 'mastered' | 'unlocked' | 'locked' {
  if (learner.personas_mastered.includes(key)) return 'mastered';
  if (learner.personas_unlocked.includes(key)) return 'unlocked';
  return 'locked';
}
